import { useMemo } from 'react';
import { UptimeCheck } from '../../lib/uptime';

interface UptimeSparklineProps {
  checks: UptimeCheck[];
  width?: number;
  height?: number;
  maxBars?: number;
}

/**
 * Per-check mini bar series — bar height tracks response time;
 * failed checks render full-height in the down color.
 */
export default function UptimeSparkline({
  checks,
  width = 110,
  height = 22,
  maxBars = 24,
}: UptimeSparklineProps) {
  const bars = useMemo(() => {
    const sorted = [...checks].sort(
      (a, b) => new Date(a.checked_at).getTime() - new Date(b.checked_at).getTime()
    );
    const recent = sorted.slice(-maxBars);
    const validTimes = recent
      .filter(c => c.is_up && c.response_time_ms !== null)
      .map(c => c.response_time_ms!);
    const max = validTimes.length > 0 ? Math.max(...validTimes, 1) : 1;

    return recent.map(check => {
      const failed = !check.is_up;
      const ms = check.response_time_ms;
      return {
        id: check.id,
        failed,
        heightPct: failed
          ? 100
          : ms === null
          ? 12
          : Math.max(12, (ms / max) * 100),
      };
    });
  }, [checks, maxBars]);

  if (bars.length === 0) {
    return (
      <span
        className="t-num"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: `${width}px`,
          height: `${height}px`,
          fontSize: 'var(--text-xs)',
          color: 'var(--text-faint)',
        }}
      >
        no data
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        gap: '2px',
        alignItems: 'flex-end',
        width: `${width}px`,
        height: `${height}px`,
      }}
      title={`Last ${bars.length} checks`}
    >
      {bars.map(bar => (
        <span
          key={bar.id}
          style={{
            flex: 1,
            height: `${bar.heightPct}%`,
            background: bar.failed ? 'var(--status-down)' : 'var(--surface-active)',
            borderRadius: '1.5px',
            opacity: bar.failed ? 0.9 : 1,
          }}
        />
      ))}
    </span>
  );
}
